import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d28fa793-233b-4071-af00-df1c1e1b0bba/artifacts/6t4csq6x_Logo_Komel%20AG.png";

const loginSchema = z.object({
  email: z.string().email("Gültige E-Mail-Adresse erforderlich"),
  password: z.string().min(1, "Passwort erforderlich")
});

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(loginSchema)
  });

  // Redirect if already logged in
  if (user) {
    navigate("/admin/dashboard");
    return null;
  }

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError("");
    
    try {
      await login(data.email, data.password);
      toast.success("Erfolgreich angemeldet");
      navigate("/admin/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      setError("Ungültige Anmeldedaten");
      toast.error("Anmeldung fehlgeschlagen");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-komel-neutral-100 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white shadow-xl p-8 md:p-10" data-testid="admin-login-container">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="Komel AG" 
              className="h-12 w-auto mx-auto mb-4"
            />
            <h1 className="font-heading text-2xl font-bold text-komel-primary">
              Admin Login
            </h1>
            <p className="text-komel-secondary text-sm mt-2">
              Melden Sie sich an, um Referenzen zu verwalten.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="login-error">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="admin-login-form">
            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-komel-primary font-medium">
                E-Mail
              </Label>
              <Input
                id="email"
                type="email"
                {...register("email")}
                placeholder="admin@komel.ch"
                className={`mt-2 rounded-none ${errors.email ? 'border-red-500' : ''}`}
                data-testid="login-email"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password" className="text-komel-primary font-medium">
                Passwort
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  {...register("password")}
                  placeholder="••••••••"
                  className={`pr-12 rounded-none ${errors.password ? 'border-red-500' : ''}`}
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-komel-secondary hover:text-komel-primary"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-komel-primary hover:bg-komel-primary-light text-white font-semibold py-6 rounded-none"
              data-testid="login-submit"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Anmeldung...
                </>
              ) : (
                "Anmelden"
              )}
            </Button>
          </form>

          {/* Back Link */}
          <div className="mt-6 text-center">
            <a 
              href="/"
              className="text-sm text-komel-secondary hover:text-komel-accent transition-colors"
              data-testid="back-to-home"
            >
              ← Zurück zur Startseite
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
